import { Suspense } from 'react';
import { BrowserRouter as Router, useRoutes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { queryClient } from '@/lib/queryClient';
import { useAuth } from '@/store/useAppStore';
import { AppLayout } from '@/layout/AppLayout';
import { Loader } from '@/components/Loader';
import { useAuthInitialization } from '@/hooks/useAuthInitialization';
import { publicLink, isPublicLinkSession } from '@/lib/supabase';
import { PublicLinkShell } from '@/features/public/components/PublicLinkShell';
import { publicRoutes, getRoleBasedRoutes, getSidebarLinks } from '@/routes';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Composant pour gérer les routes authentifiées
const AuthenticatedRoutes = () => {
  const { profile } = useAuth();

  const routes = useRoutes(
    profile ? getRoleBasedRoutes(profile.role, profile.permissions) : []
  );

  return routes;
};

// Composant pour gérer les routes publiques
const PublicRoutes = () => {
  const routes = useRoutes(publicRoutes);
  return routes;
};

const FullScreenLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader size="lg" />
  </div>
);

const AppShell = () => {
  const { session, profile, loading } = useAuth();

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!session || !profile) {
    return <PublicRoutes />;
  }

  return (
    <AppLayout sidebarLinks={getSidebarLinks(profile.role, profile.permissions)}>
      <AuthenticatedRoutes />
    </AppLayout>
  );
};

/**
 * Application principale refactorisée
 * PERF-003: Utilisation de useAuth avec shallow comparison
 */
function App() {
  // Une visite par lien public n'a pas de session à restaurer : l'amorçage
  // d'authentification est neutralisé pour ne pas écraser le profil synthétique.
  useAuthInitialization({ enabled: !isPublicLinkSession });

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster
        position="top-right"
        toastOptions={{ style: { marginBottom: 0 } }}
        containerStyle={{ top: 16, right: 16, zIndex: 100 }}
      />
      {/*
        Le basename fait tourner toute l'application sous /p/<slug>-<token> pour
        une visite par lien : les chemins et les liens internes (`/projects`,
        `/tache`...) restent inchangés et se préfixent automatiquement.
      */}
      <Router basename={publicLink ? `/p/${publicLink.handle}` : undefined}>
        <ErrorBoundary name="Global">
          <Suspense fallback={<FullScreenLoader />}>
            {isPublicLinkSession ? <PublicLinkShell /> : <AppShell />}
          </Suspense>
        </ErrorBoundary>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
